import React, { useState } from 'react';
import { MdLightbulbOutline, MdDeleteOutline, MdArrowBack } from 'react-icons/md';
import { FaGraduationCap, FaHome, FaCar, FaPlane, FaPiggyBank } from 'react-icons/fa';
import { useProfile } from '../contexts/ProfileContext';
import data from '../data/goals.json';

// BeHuman accent palette — rotated so successive goals get distinct hues.
// Hex literals (see styles/DESIGN.md note about Recharts/SVG fill).
const GOAL_COLORS = [
  '#2FBF00', // td-green
  '#946E9F', // td-lavender
  '#7C5636', // td-brown
  '#CFBD91', // td-gold
  '#448889', // td-teal
  '#462992', // td-purple
];

function goalIcon(title) {
  const t = (title || '').toLowerCase();
  if (t.includes('loan') || t.includes('student') || t.includes('debt') || t.includes('education')) return <FaGraduationCap />;
  if (t.includes('home') || t.includes('house') || t.includes('down payment') || t.includes('mortgage')) return <FaHome />;
  if (t.includes('car') || t.includes('vehicle') || t.includes('auto')) return <FaCar />;
  if (t.includes('travel') || t.includes('vacation') || t.includes('trip') || t.includes('flight')) return <FaPlane />;
  return <FaPiggyBank />;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonthYear = (d) => `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;

// A goal's stated target date, if any of the common fields carry one.
function parseTargetDate(goal) {
  const raw = goal.target_date || goal.targetDate || goal.by || goal.deadline;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d) ? null : d;
}

function humanDuration(months) {
  if (months <= 0) return 'due now';
  if (months < 12) return `${months} mo${months > 1 ? 's' : ''}`;
  const y = Math.floor(months / 12), m = months % 12;
  return m ? `${y}y ${m}mo` : `${y} year${y > 1 ? 's' : ''}`;
}

// Savings "parking spot" rates (illustrative sample data — mirrors the backend
// planner in lib/daily/plan.js and the Savings rows on TodaysRatesScreen). A GIC
// that locks past the goal horizon is ineligible (the cash is needed sooner).
const SAVINGS_VEHICLES = [
  { name: 'High Interest Savings', kind: 'HISA', rate: 0.035, lockMonths: 0 },
  { name: 'TFSA Savings', kind: 'TFSA', rate: 0.0325, lockMonths: 0 },
  { name: 'GIC — 1 year', kind: 'GIC', rate: 0.041, lockMonths: 12 },
  { name: 'GIC — 5 year', kind: 'GIC', rate: 0.0445, lockMonths: 60 },
];
// Highest-yield vehicle whose lock fits within `months`, with projected simple
// interest on `amount` over that horizon. Null when there's nothing to park.
function bestVehicle(amount, months) {
  if (!(amount > 0)) return null;
  const pool = SAVINGS_VEHICLES.filter((v) => v.lockMonths <= months || months === 0);
  const list = pool.length ? pool : SAVINGS_VEHICLES.filter((v) => v.lockMonths === 0);
  if (!list.length) return null;
  const best = list.reduce((a, b) => (b.rate > a.rate ? b : a));
  const interest = Math.round(amount * best.rate * (months / 12));
  return { ...best, interest };
}
// The customer's reward-point balance from whichever persona field carries it.
function pointsBalance(profile) {
  const c = [
    profile?.rewards?.points, profile?.rewards?.balance,
    profile?.rewardsPoints, profile?.pointsBalance, profile?.points,
    profile?.accounts?.credit_card?.rewards_balance,
    profile?.accounts?.credit_card?.points_balance,
  ];
  for (const v of c) { const n = Number(v); if (Number.isFinite(n) && n > 0) return n; }
  return null;
}

// Flight fare finder (plan 160) — a compact client-side mirror of lib/daily/
// flights.js so a TRAVEL goal can show the cheapest fare found next to its savings
// plan. SAMPLE DATA ONLY — not a live quote. Each dest carries the cheapest cash
// fare and the lowest-miles points fare from the backend catalog.
const FLIGHT_SAMPLES = {
  tokyo: { dest: 'Tokyo', cashFare: 1450, cashCarrier: 'Air Canada', miles: 100000, surcharge: 150, ptsCarrier: 'ANA' },
  japan: { dest: 'Tokyo', cashFare: 1450, cashCarrier: 'Air Canada', miles: 100000, surcharge: 150, ptsCarrier: 'ANA' },
  vancouver: { dest: 'Vancouver', cashFare: 380, cashCarrier: 'Flair', miles: 20000, surcharge: 40, ptsCarrier: 'WestJet' },
  london: { dest: 'London', cashFare: 890, cashCarrier: 'Air Canada', miles: 55000, surcharge: 110, ptsCarrier: 'British Airways' },
  montreal: { dest: 'Montreal', cashFare: 190, cashCarrier: 'Porter', miles: 12000, surcharge: 25, ptsCarrier: 'Air Canada' },
  'new york': { dest: 'New York', cashFare: 300, cashCarrier: 'Porter', miles: 18000, surcharge: 40, ptsCarrier: 'Air Canada' },
};
// The cheapest sample fare for a goal whose title names a known destination, or null.
function cheapestFareForGoal(goal) {
  const t = (goal.title || '').toLowerCase();
  for (const [key, fare] of Object.entries(FLIGHT_SAMPLES)) {
    if (t.includes(key)) return fare;
  }
  return null;
}

// Plain-language "why this matters" line, tailored to the goal type. Debt goals
// frame the interest avoided per dollar; education goals the grant match; savings
// goals the growth + pace. Figures are illustrative defaults (no per-goal APR yet).
function goalMeaning(goal, current, remaining, monthly, projDate) {
  const t = (goal.title || '').toLowerCase();
  if (remaining <= 0) return "Goal reached — nice work. Consider rolling this contribution into your next goal.";
  if (/credit|card|loan|debt|student|payoff|pay off/.test(t)) {
    const apr = /credit|card/.test(t) ? 19.99 : 6.0;
    const perDollar = (apr / 100).toFixed(2);
    const extraSaved = Math.round(100 * (apr / 100));
    return `Every $1 you pay down avoids about $${perDollar} in interest each year (~${apr}% APR). Putting an extra $100/mo here saves roughly $${extraSaved}/yr in interest and clears it sooner.`;
  }
  if (/resp|tuition|college|university|education/.test(t)) {
    return 'Every $1 you contribute can earn $0.20 in CESG government grant — up to $500 a year in free money.';
  }
  const yearGrowth = Math.round(current * 0.03);
  return `At ~3% interest your $${current.toLocaleString()} saved earns about $${yearGrowth.toLocaleString()}/yr. Adding $${monthly.toLocaleString()}/mo keeps you on pace for ${fmtMonthYear(projDate)}.`;
}

// Daily check-in streak (plan 280) — the current consecutive-day run of check-ins
// and the latest check-in's met/total, read from the replayed `checkins` on the
// working profile (appended by the daily_checkin action, so it survives reloads).
// Mirrors lib/daily/checkin.js's streakState.
function checkinStreak(profile) {
  const list = Array.isArray(profile?.checkins) ? profile.checkins : [];
  const days = [...new Set(
    list.map((c) => String(c.date || c.at || '').slice(0, 10)).filter(Boolean),
  )].sort();
  if (!days.length) return null;
  const toNum = (d) => Math.floor(new Date(`${d}T00:00:00Z`).getTime() / 86400000);
  let current = 1;
  for (let i = days.length - 1; i > 0; i--) {
    if (toNum(days[i]) - toNum(days[i - 1]) === 1) current += 1;
    else break;
  }
  const last = list.find((c) => String(c.date || c.at || '').slice(0, 10) === days[days.length - 1]) || {};
  return { current, lastCheckin: days[days.length - 1], metCount: last.metCount, totalCount: last.totalCount };
}

const GoalsScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData, setLoadedProfileData } = useProfile();
  const streak = checkinStreak(loadedProfileData);
  const defaultGoalsData = data.goalsData || [
    { title: 'Pay Off Student Loan', current: 2100,  target: 25000, status: 'In Progress' },
    { title: 'Home Down Payment',    current: 18352, target: 60000, status: 'On Track' },
  ];

  // Goals come from the loaded profile when present; otherwise we show the demo
  // defaults. Deleting a profile goal writes back through the profile context so
  // it sticks while navigating; deleting a demo default is kept in local state
  // (no profile to persist to) so we don't fabricate a fake profile object.
  const hasProfileGoals = !!loadedProfileData?.goals?.length;
  const [demoGoals, setDemoGoals] = useState(defaultGoalsData);
  const goalsData = hasProfileGoals ? loadedProfileData.goals : demoGoals;

  const deleteGoal = (idx) => {
    const g = goalsData[idx];
    if (!window.confirm(`Delete "${g?.title || 'this goal'}"? This can't be undone.`)) return;
    if (hasProfileGoals) {
      setLoadedProfileData(prev => ({ ...prev, goals: prev.goals.filter((_, i) => i !== idx) }));
    } else {
      setDemoGoals(prev => prev.filter((_, i) => i !== idx));
    }
  };

  return (
    <div className="screen bh-flow goals-screen">
      <header className="bh-flow-hero tall">
        <div className="bh-flow-hero-row">
          <div className="bh-flow-hero-left">
            <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('services'))} aria-label="Back">
              <MdArrowBack />
            </button>
            <h1 className="bh-flow-title">Goals</h1>
          </div>
        </div>
        {loadedProfileData?.name && (
          <p className="bh-flow-hero-subtitle" style={{ color: 'var(--bh-black)' }}>
            Profile: {loadedProfileData.name}
          </p>
        )}
      </header>

      <div className="bh-flow-body">
        {/* Daily check-in streak (plan 280) — today's progress + consecutive-day
            run, so goal tracking feels like a habit, not a one-off. */}
        {streak && (
          <div className="bh-form-card" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 'var(--space-sm)',
          }}>
            <div>
              <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                Today
              </div>
              <div style={{ font: 'var(--bh-h3)', color: 'var(--bh-black)', marginTop: 2 }}>
                {(streak.metCount != null && streak.totalCount != null)
                  ? `${streak.metCount} of ${streak.totalCount} on track`
                  : 'Checked in'}
              </div>
            </div>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'var(--td-green-light)', color: 'var(--td-dark-green)',
              font: 'var(--bh-caption)', fontWeight: 700,
              padding: '6px 12px', borderRadius: 'var(--radius-full)',
            }}>
              🔥 {streak.current}-day streak
            </span>
          </div>
        )}

        {goalsData.map((goal, idx) => {
          const current = goal.current ?? goal.current_amount ?? goal.saved ?? 0;
          const target  = goal.target ?? goal.target_amount ?? 0;
          const pct = target > 0 ? Math.min((current / target) * 100, 100) : 0;
          const goalColor = goal.color || GOAL_COLORS[idx % GOAL_COLORS.length];
          const remaining = Math.max(0, target - current);

          // Timing — show the stated target date when present, otherwise project a
          // finish date from an assumed monthly contribution (≈2-year default pace).
          const targetDate = parseTargetDate(goal);
          const monthly = goal.monthlyContribution || goal.monthly || Math.max(50, Math.round(remaining / 24));
          const projMonths = remaining > 0 ? Math.ceil(remaining / monthly) : 0;
          const projDate = new Date();
          projDate.setMonth(projDate.getMonth() + projMonths);
          const monthsLeft = targetDate
            ? Math.max(0, Math.round((targetDate - new Date()) / (1000 * 60 * 60 * 24 * 30.44)))
            : projMonths;
          const meaning = goalMeaning(goal, current, remaining, monthly, projDate);

          // Savings plan (plan 260) — the monthly needed to hit the target by its
          // date, whether the planned pace is on track, the best parking vehicle +
          // projected interest, and any reward-points offset. Only shown for
          // fundable savings goals (a remaining balance to save toward).
          const requiredMonthly = remaining > 0 && monthsLeft > 0
            ? Math.round(remaining / monthsLeft)
            : remaining;
          const plannedMonthly = goal.monthlyContribution || goal.monthly || null;
          const onTrack = plannedMonthly != null ? plannedMonthly >= requiredMonthly : null;
          const vehicle = remaining > 0 ? bestVehicle(remaining, monthsLeft) : null;
          const pts = pointsBalance(loadedProfileData);
          const pointsValue = pts != null ? Math.round(pts * 1.5 / 100) : null;
          const fare = cheapestFareForGoal(goal); // travel goal → cheapest sample fare (plan 160)
          const showPlan = remaining > 0;

          return (
            <div key={idx} className="bh-form-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2xs)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2xs)', minWidth: 0 }}>
                  <div
                    style={{
                      width: 44, height: 44, borderRadius: 'var(--radius-full)',
                      background: goalColor, color: 'var(--bh-white)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.25rem', flexShrink: 0,
                    }}
                  >
                    {goalIcon(goal.title)}
                  </div>
                  <h3 style={{ margin: 0, font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>{goal.title}</h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2xs)', flexShrink: 0 }}>
                  <span style={{ font: 'var(--bh-h2)', fontWeight: 800, color: goalColor, letterSpacing: '-0.01em' }}>
                    {pct.toFixed(0)}%
                  </span>
                  <button
                    type="button"
                    onClick={() => deleteGoal(idx)}
                    aria-label={`Delete goal ${goal.title}`}
                    title="Delete goal"
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--bh-gray)', display: 'inline-flex', alignItems: 'center',
                      padding: 4, borderRadius: 'var(--radius-full)',
                    }}
                  >
                    <MdDeleteOutline size={18} />
                  </button>
                </div>
              </div>

              {/* Progress bar */}
              <div
                style={{
                  width: '100%', height: 8, borderRadius: 'var(--radius-full)',
                  background: 'var(--bh-light-gray)', overflow: 'hidden',
                }}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  style={{
                    width: `${pct}%`, height: '100%',
                    background: goalColor,
                    borderRadius: 'var(--radius-full)',
                    transition: 'width 200ms ease-in-out',
                  }}
                />
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
                <div>
                  <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                    Paid to date
                  </div>
                  <div style={{ font: 'var(--bh-h3)', color: 'var(--bh-black)', marginTop: 2 }}>
                    ${current.toLocaleString()}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                    Remaining
                  </div>
                  <div style={{ font: 'var(--bh-h3)', color: 'var(--bh-black)', marginTop: 2 }}>
                    ${remaining.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Timing — target date (or projected finish) + time to go */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
                <div>
                  <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                    {targetDate ? 'Target date' : 'Projected finish'}
                  </div>
                  <div style={{ font: 'var(--bh-h3)', color: 'var(--bh-black)', marginTop: 2 }}>
                    {remaining <= 0 ? 'Reached' : fmtMonthYear(targetDate || projDate)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                    {remaining <= 0 ? '' : targetDate ? 'Time to go' : `At ~$${monthly.toLocaleString()}/mo`}
                  </div>
                  <div style={{ font: 'var(--bh-h3)', color: goalColor, marginTop: 2 }}>
                    {remaining <= 0 ? '🎉' : humanDuration(monthsLeft)}
                  </div>
                </div>
              </div>

              {goal.status && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center',
                  background: 'var(--td-green-light)', color: 'var(--td-dark-green)',
                  font: 'var(--bh-caption)', fontWeight: 600,
                  padding: '4px 10px', borderRadius: 'var(--radius-full)',
                  alignSelf: 'flex-start',
                }}>
                  {goal.status}
                </div>
              )}

              {/* Savings plan (plan 260) — required monthly + on-track badge, the
                  recommended parking vehicle, and any reward-points offset. */}
              {showPlan && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    <div>
                      <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                        Suggested monthly
                      </div>
                      <div style={{ font: 'var(--bh-h3)', color: 'var(--bh-black)', marginTop: 2 }}>
                        ${requiredMonthly.toLocaleString()}/mo
                      </div>
                    </div>
                    {onTrack != null && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        background: onTrack ? 'var(--td-green-light)' : 'var(--td-amber-light, #FDECC8)',
                        color: onTrack ? 'var(--td-dark-green)' : 'var(--td-brown)',
                        font: 'var(--bh-caption)', fontWeight: 600,
                        padding: '4px 10px', borderRadius: 'var(--radius-full)',
                      }}>
                        {onTrack ? 'On track' : `$${Math.max(0, requiredMonthly - plannedMonthly).toLocaleString()}/mo behind`}
                      </span>
                    )}
                  </div>

                  {vehicle && (
                    <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                      Best rate: {vehicle.name} at {(vehicle.rate * 100).toFixed(2)}% → ~${vehicle.interest.toLocaleString()} interest by {fmtMonthYear(targetDate || projDate)}.
                    </div>
                  )}
                  {pointsValue != null && (
                    <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                      Points offset: {pts.toLocaleString()} pts ≈ ${pointsValue.toLocaleString()} toward this goal.
                    </div>
                  )}
                  {/* Flight fare finder (plan 160) — cheapest sample fare for a
                      travel goal, and whether the customer's points could cover it. */}
                  {fare && (
                    <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                      Cheapest fare found: ${fare.cashFare.toLocaleString()} cash ({fare.cashCarrier}) or {fare.miles.toLocaleString()} pts + ${fare.surcharge} ({fare.ptsCarrier}).
                      {pts != null && (pts >= fare.miles
                        ? ' Your points can cover it.'
                        : ` You're ${(fare.miles - pts).toLocaleString()} pts short.`)}
                    </div>
                  )}
                  <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)', fontStyle: 'italic' }}>
                    Estimates based on current rates, subject to change.
                  </div>
                </div>
              )}

              <div className="bh-info-box" style={{ display: 'flex', gap: 'var(--space-2xs)', alignItems: 'flex-start' }}>
                <MdLightbulbOutline size={16} style={{ color: 'var(--td-amber)', flexShrink: 0, marginTop: 2 }} />
                <span>{meaning}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GoalsScreen;
